import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

function GrandparentConfirmDialog({
    open,
    onOpenChange,
    title,
    grandparentsList,
    onConfirm,
    loading,
    errorMessage,
}) {
    const handleClose = () => {
        onOpenChange?.(false);
    };

    const handleConfirm = () => {
        onConfirm?.();
    };

    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    className={cn(
                        'fixed inset-0 z-[250] bg-black/90 backdrop-blur-sm',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
                    )}
                />
                <DialogPrimitive.Content
                    onInteractOutside={(e) => e.preventDefault()}
                    className={cn(
                        'fixed left-[50%] top-[50%] z-[300] w-full max-w-2xl max-h-[90vh] overflow-y-auto',
                        'translate-x-[-50%] translate-y-[-50%]',
                        'bg-card border border-border rounded-lg shadow-lg p-4 sm:p-6',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
                    )}
                >
                    <DialogPrimitive.Close
                        className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
                        onClick={handleClose}
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>

                    <div className="mb-4 pr-8">
                        <DialogPrimitive.Title className="text-xl font-semibold text-primary">
                            {title || 'Confirm Grandparent Assignment'}
                        </DialogPrimitive.Title>
                        <DialogPrimitive.Description className="text-sm text-muted-foreground mt-2">
                            Please note the following distributor will be added as a Grandparent to the selected group number.
                        </DialogPrimitive.Description>
                    </div>

                    <div className="py-2">
                        {grandparentsList && Array.isArray(grandparentsList) && grandparentsList.length > 0 ? (
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/30">
                                            <TableHead className="font-semibold">User Name</TableHead>
                                            <TableHead className="font-semibold">Full Name</TableHead>
                                            <TableHead className="font-semibold text-right">Commission ($)</TableHead>
                                            <TableHead className="font-semibold text-center">2nd Level</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {grandparentsList.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell className="font-medium">
                                                    {row.parent_UserName}
                                                </TableCell>
                                                <TableCell>
                                                    {row.parent_FullName}
                                                </TableCell>
                                                <TableCell className="text-right font-mono">
                                                    {row.parent_GcComm}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {row.parent_IsAllowedForGC ? (
                                                        <Badge variant="success">Yes</Badge>
                                                    ) : (
                                                        <Badge variant="secondary">No</Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No grandparent data available
                            </div>
                        )}

                        {errorMessage && (
                            <Alert variant="destructive" className="mt-4">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{errorMessage}</AlertDescription>
                            </Alert>
                        )}
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleConfirm}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Processing
                                </>
                            ) : (
                                'Proceed'
                            )}
                        </Button>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}

export default GrandparentConfirmDialog;
